import { useCallback, useMemo } from "react";
import { differenceInCalendarDays, displayDate } from "helpers/time";
import { StripeStatus } from "views/page-content/platforms/types";
import { useSelector } from "react-redux";
import { RootState } from "store/reducers";
import { MessageContentState } from "../../../types";
import { useSupportSystem } from "hooks/useSupportSystem";
import { useUserStripeData } from "hooks/useUserStripeData";
import { useDispatch } from "react-redux";
import { setActiveModal } from "store/modal/slice";
import { Modals } from "store/modal/types";
import { StripeEmbeddedModal } from "components/modal/StripeEmbeddedModal";
import { GatedContentStatus } from "store/stripeConnectAccount/types";
import { t } from "i18next";
import { useNavigate } from "react-router-dom";
import { usePauseStatus } from "hooks/usePauseStatus";

export const useSubscriptionStatusBannerMessageProps = (
    onLinkClick: () => void
) => {
    const { isSupportAvailable, supportSystemCommand } = useSupportSystem();
    const { userInfo, orgs, defaultOrg } = useSelector(
        (s: RootState) => s.user
    );
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const {
        accountData: { gatedContentStatus }
    } = useUserStripeData({
        includeProducts: false,
        requestImmediately: true
    });

    const { userIsPaused, userIsOrWillPause } = usePauseStatus();

    const openStripeEmbeddedModal = useCallback(() => {
        dispatch(
            setActiveModal({
                id: "stripe-embedded-modal",
                type: Modals.StripeEmbeddedModal,
                component: <StripeEmbeddedModal />
            })
        );
    }, [dispatch]);

    const navigateToManagePlan = useCallback(() => {
        navigate("/subscription#openModal");
    }, [navigate]);

    const isShopifyPlan = useMemo<boolean>(
        () =>
            userInfo?.ActiveProduct?.Details?.Discriminator?.includes(
                "Shopify"
            ),
        [userInfo]
    );

    const stripeConnectionBannerProps = useMemo<MessageContentState>(() => {
        return {
            leftMessageProps: {
                key: t("stripe-bannner:problems-with-configuration-header"),
                subheader: t(
                    "stripe-bannner:problems-with-configuration-subheader"
                )
            },
            linkProps: {
                href: "#",
                text: t("stripe-bannner:resolve-issues"),
                onClick: openStripeEmbeddedModal
            }
        };
    }, [openStripeEmbeddedModal]);

    const pausedBannerProps = useMemo<MessageContentState>(() => {
        return {
            leftMessageProps: {
                key: t("pause-copy:paused-subscription")
            },
            linkProps: {
                href: "#",
                text: isSupportAvailable && t("pause-copy:contact-support"),
                onClick: () =>
                    isSupportAvailable && supportSystemCommand("show")
            },
            rightMessage: ""
        };
    }, [isSupportAvailable, supportSystemCommand]);

    const pausedBannerPropsV2 =
        useMemo<MessageContentState>((): MessageContentState => {
            if (userIsPaused) {
                // User is currently in a paused state
                return {
                    leftMessageProps: {
                        key: t("status-banner:during-pause:header", {
                            date: displayDate(
                                userInfo?.Plan?.Schedule?.Details
                                    ?.PausedEndDate,
                                "en-US",
                                "dddd MMMM D, YYYY"
                            )
                        }),
                        subheader: t("status-banner:during-pause:subheader")
                    },
                    buttonProps: {
                        label: t("status-banner:during-pause:button-label"),
                        onClick: navigateToManagePlan,
                        type: "btn-outline-primary",
                        className: "upgrade-button"
                    },
                    cssClass: "switcher-player-studio-promo",
                    hideIcon: true,
                    preventDismiss: true
                };
            } else {
                // User is about to enter a paused state
                return {
                    leftMessageProps: {
                        key: t("status-banner:before-pause:header", {
                            date: displayDate(
                                userInfo?.Plan?.Schedule?.Details
                                    ?.PausedBeginDate,
                                "en-US",
                                "dddd MMMM D, YYYY"
                            )
                        }),
                        subheader: t("status-banner:before-pause:subheader")
                    },
                    buttonProps: {
                        label: t("status-banner:before-pause:button-label"),
                        onClick: navigateToManagePlan,
                        type: "btn-outline-primary",
                        className: "upgrade-button"
                    },
                    cssClass: "switcher-player-studio-promo",
                    hideIcon: true,
                    preventDismiss: true
                };
            }
        }, [navigateToManagePlan, userInfo, userIsPaused]);

    const trialBannerProps = useMemo<MessageContentState>(() => {
        const count = differenceInCalendarDays(
            new Date(userInfo?.Plan?.Details?.ActiveUntil),
            new Date(),
            true
        );

        const key = t(
            count > 1
                ? t("status-banner:days-remaining_plural")
                : t("status-banner:days-remaining"),
            { count }
        );

        if (userInfo?.Plan?.Details?.Name === "Trial") {
            return {
                leftMessageProps: {
                    key
                },
                linkProps: isShopifyPlan
                    ? undefined
                    : {
                          href: "/subscription#openModal",
                          text: t("status-banner:no-active-link"),
                          onClick: onLinkClick
                      }
            };
        } else {
            return {
                leftMessageProps: {
                    key
                },
                linkProps: {
                    href: `/subscription?planId=${userInfo?.Plan?.Details?.Id}`,
                    text: t("status-banner:start-now-link"),
                    end: true,
                    onClick: onLinkClick
                },
                rightMessage: t("status-banner:start-now-right")
            };
        }
    }, [
        isShopifyPlan,
        onLinkClick,
        userInfo?.Plan?.Details?.ActiveUntil,
        userInfo.Plan?.Details?.Id,
        userInfo.Plan?.Details?.Name
    ]);

    const incompleteBannerProps = useMemo<MessageContentState>(() => {
        return {
            leftMessageProps: {
                key: t("status-banner:could-not-charge")
            },
            linkProps: {
                href: `/subscription`,
                text: t("status-banner:try-again"),
                onClick: onLinkClick
            },
            rightMessage: ""
        };
    }, [onLinkClick]);

    const endedBannerProps = useMemo<MessageContentState>(() => {
        if (orgs.length && defaultOrg === null) {
            return {
                leftMessageProps: {
                    key: t("status-banner:did-you-mean-org")
                },
                linkProps: {
                    href: "/workspaces",
                    text: t("status-banner:change-workspace-link"),
                    onClick: onLinkClick
                }
            };
        } else {
            return {
                leftMessageProps: {
                    key: t("status-banner:no-active-left")
                },
                linkProps: {
                    href: "/subscription",
                    text: t("status-banner:no-active-link"),
                    onClick: onLinkClick
                }
            };
        }
    }, [defaultOrg, onLinkClick, orgs.length]);

    const messageContentState = useMemo<MessageContentState>(() => {
        if (
            gatedContentStatus === GatedContentStatus.STRIPE_ACCOUNT_UNVERIFIED
        ) {
            return stripeConnectionBannerProps;
        } else if (userInfo?.Claims?.Details?.Roles?.includes("Paused")) {
            return pausedBannerProps;
        } else if (userIsOrWillPause) {
            return pausedBannerPropsV2;
        } else {
            switch (userInfo?.Plan?.Details?.Status) {
                case StripeStatus.Trialing:
                    return trialBannerProps;

                case StripeStatus.Incomplete:
                case StripeStatus.PastDue:
                    return incompleteBannerProps;

                case StripeStatus.Ended:
                    return endedBannerProps;
                default:
                    if (userInfo?.Plan?.Details?.Name === "Trial")
                        return trialBannerProps;
                    if (userInfo?.Plan?.Details?.Name === "No Subscription")
                        return endedBannerProps;
            }
        }
    }, [
        endedBannerProps,
        gatedContentStatus,
        incompleteBannerProps,
        pausedBannerProps,
        pausedBannerPropsV2,
        stripeConnectionBannerProps,
        trialBannerProps,
        userInfo,
        userIsOrWillPause
    ]);

    return { messageContentState, gatedContentStatus };
};
